import { UnstyledButton, Group, Stack, Text, Box, Badge, Code, Loader } from "@mantine/core";
import { Paperclip, Mail, Clock, Check, X, AlertTriangle, LucideIcon } from "lucide-react";
import { EmailLog } from "../../services/logService.js";
import { getStatusColor, getRelativeTime, getStatusLabel, formatTimestamp } from "../../utils/formatters.js";

interface EventListItemProps {
  log: EmailLog;
  isSelected: boolean;
  onClick: () => void;
}

const statusIcons: Record<string, LucideIcon> = {
  received: Mail,
  processing: Clock,
  completed: Check,
  error: X,
  rejected: X,
  warning: AlertTriangle,
};

export function EventListItem({ log, isSelected, onClick }: EventListItemProps) {
  const attachmentCount = log.attachments?.length || 0;
  const statusColor = getStatusColor(log.status);
  const statusLabel = getStatusLabel(log.status);
  const StatusIcon = statusIcons[log.status] || Mail;

  return (
    <UnstyledButton
      onClick={onClick}
      style={{
        position: "relative",
        width: "100%",
        padding: "10px 12px 10px 16px",
        borderRadius: "8px",
        backgroundColor: isSelected ? "var(--mantine-color-blue-light)" : "transparent",
        transition: "all 0.15s ease",
        border: isSelected ? "1px solid var(--mantine-color-blue-outline)" : "1px solid transparent",
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = "var(--mantine-color-gray-light)";
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = "transparent";
        }
      }}
    >
      {/* 左側のステータスカラーバー */}
      <Box
        style={{
          position: "absolute",
          left: "6px",
          top: "8px",
          bottom: "8px",
          width: "4px",
          borderRadius: "4px",
          backgroundColor: isSelected ? "var(--mantine-color-blue-filled)" : `var(--mantine-color-${statusColor}-filled)`,
          opacity: isSelected ? 1 : 0.6,
        }}
      />

      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" fw={600} lineClamp={2} pr={4}>
            {log.customerName || "不明な顧客"} {log.eventTypeName || "イベント"}
          </Text>
          
          <Group gap={8} wrap="wrap" style={{ rowGap: "2px" }}>
            <Code 
              style={{ 
                fontSize: "12px", 
                fontWeight: 700, 
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                letterSpacing: "0.05em",
                flexShrink: 0,
                padding: "1px 6px",
              }}
            >
              {log.id.slice(0, 8)}
            </Code>
            <Group gap={4} style={{ flexShrink: 0 }}>
              <Text size="xs">
                {formatTimestamp(log.receivedAt)}
              </Text>
              <Text size="xs" c="dimmed">
                • {getRelativeTime(log.receivedAt)}
              </Text>
            </Group>
            {attachmentCount > 0 && (
              <Group gap={2} style={{ flexShrink: 0 }}>
                <Paperclip size={12} />
                <Text size="xs">{attachmentCount}</Text>
              </Group>
            )}
          </Group>
        </Stack>
        <Badge 
          size="sm" 
          color={statusColor} 
          variant={isSelected ? "filled" : "light"}
          leftSection={['processing', 'received'].includes(log.status) ? <Loader size={12} color={isSelected ? "white" : statusColor} type="oval" /> : <StatusIcon size={12} />}
          style={{ 
            flexShrink: 0, 
            marginLeft: "4px",
          }}
        >
          {statusLabel}
        </Badge>
      </Group>
    </UnstyledButton>
  );
}
